import { useState, useRef, useEffect } from "preact/hooks";

const MAX_LEN = 2000;
// Show a character counter only when the user is close to the limit.
const COUNTER_THRESHOLD = 0.9;

// Strip characters that have no legitimate use in a Q&A and are common
// vectors for text spoofing (bidirectional overrides), hidden content
// (zero-width chars), or weird rendering bugs (C0 control bytes). Tab
// (U+0009) and newline (U+000A) are preserved.
const SANITIZE_RE = new RegExp(
  "[" +
    "\\u0000-\\u0008\\u000B-\\u001F\\u007F" +
    "\\u202A-\\u202E\\u2066-\\u2069" +
    "\\u200B-\\u200D\\uFEFF" +
  "]",
  "g",
);

function sanitize(text: string): string {
  return text.replace(SANITIZE_RE, "");
}

interface Props {
  onSubmit: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function InputBox({ onSubmit, disabled, placeholder }: Props) {
  const [value, setValue] = useState("");
  const ref = useRef<HTMLTextAreaElement>(null);

  function setClamped(next: string) {
    setValue(next.length > MAX_LEN ? next.slice(0, MAX_LEN) : next);
  }

  function handleSubmit() {
    const cleaned = sanitize(value).trim();
    if (!cleaned || disabled) return;
    onSubmit(cleaned);
    setValue("");
  }

  function handleKey(e: KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }

  function handlePaste(e: ClipboardEvent) {
    // Override default paste so we can strip rich text/HTML and reject
    // any non-text payload (screenshots, files, etc.).
    e.preventDefault();
    const cd = e.clipboardData;
    if (!cd) return;
    if (cd.files && cd.files.length > 0) return;
    const text = sanitize(cd.getData("text/plain") || "");
    if (!text) return;

    const ta = ref.current;
    if (!ta) {
      setClamped(value + text);
      return;
    }
    const start = ta.selectionStart ?? value.length;
    const end = ta.selectionEnd ?? value.length;
    const replacedLen = end - start;
    const remaining = MAX_LEN - (value.length - replacedLen);
    const insert = text.slice(0, Math.max(0, remaining));
    const next = value.slice(0, start) + insert + value.slice(end);
    setClamped(next);
    const cursor = start + insert.length;
    queueMicrotask(() => {
      ta.selectionStart = ta.selectionEnd = Math.min(cursor, MAX_LEN);
    });
  }

  function handleDragOver(e: DragEvent) {
    if (e.dataTransfer && Array.from(e.dataTransfer.types).includes("Files")) {
      e.preventDefault();
    }
  }

  function handleDrop(e: DragEvent) {
    if (e.dataTransfer && e.dataTransfer.files.length > 0) {
      e.preventDefault();
    }
  }

  // Auto-resize: grow with content up to max-height defined in CSS.
  useEffect(() => {
    const ta = ref.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 120) + "px";
  }, [value]);

  // Re-focus the textarea when streaming finishes — when `disabled`
  // transitions from true back to false. `preventScroll` is essential: a
  // bare focus() makes the browser scroll the focused element into view by
  // walking up every scrollable ancestor, including the outer window. With
  // the widget embedded inline in a tall host page that yanks the whole page
  // down the instant the answer completes (and focus-scrolling is always an
  // instant jump, never smooth). The Panel already handles keeping the new
  // turn in view via a contained, smooth scroll; here we only want the
  // caret back in the box so the next question can be typed immediately.
  const wasDisabledRef = useRef<boolean>(disabled ?? false);
  useEffect(() => {
    const now = disabled ?? false;
    if (wasDisabledRef.current && !now) {
      ref.current?.focus({ preventScroll: true });
    }
    wasDisabledRef.current = now;
  }, [disabled]);

  const showCounter = value.length >= MAX_LEN * COUNTER_THRESHOLD;
  const atLimit = value.length >= MAX_LEN;

  return (
    <div class="input">
      <div class="input-wrap">
        <textarea
          ref={ref}
          class="input-textarea"
          value={value}
          onInput={(e) => setClamped((e.target as HTMLTextAreaElement).value)}
          onKeyDown={handleKey}
          onPaste={handlePaste}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          placeholder={placeholder ?? "Ask a question..."}
          rows={1}
          disabled={disabled}
          maxLength={MAX_LEN}
        />
        {showCounter && (
          <div class={`input-counter${atLimit ? " at-limit" : ""}`}>
            {value.length} / {MAX_LEN}
          </div>
        )}
      </div>
      <button
        class="input-send"
        onClick={handleSubmit}
        disabled={disabled || !value.trim()}
        aria-label="Send"
      >
        <SendIcon />
      </button>
    </div>
  );
}

function SendIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      stroke-width="2.5"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}
